
import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { SupabaseService } from '../../services/supabase.service';
import { OnboardingApplication, OnboardingWebhook } from '../../models/application';
import { AuthService } from '../../services/auth.service';
import { RealtimeChannel } from '@supabase/supabase-js';

type Status = OnboardingApplication['status'];
type SortOption = 'updated_at_desc' | 'created_at_desc' | 'created_at_asc';
type ColumnKey = 'draft' | 'submitted' | 'in_review' | 'approved' | 'ready_for_go_live' | 'live' | 'suspended' | 'rejected';

@Component({
  selector: 'app-application-list',
  templateUrl: './application-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterLink]
})
export class ApplicationListComponent implements OnDestroy {
  private supabaseService = inject(SupabaseService);
  private authService = inject(AuthService);
  private router: Router = inject(Router);
  private channels: RealtimeChannel[] = [];

  reviewer = this.authService.currentUser;

  applications = signal<OnboardingApplication[]>([]);
  loading = signal<boolean>(true);
  error = signal<string | null>(null);

  appToDelete = signal<{ id: string; name: string } | null>(null);
  isDeleting = signal<boolean>(false);
  deleteError = signal<string | null>(null);

  searchTerm = signal<string>('');
  sortOption = signal<SortOption>('updated_at_desc');
  
  columnState = signal<Record<ColumnKey, boolean>>({
    draft: false,
    submitted: false,
    in_review: false,
    approved: false,
    ready_for_go_live: false,
    live: true, // Collapsed by default
    suspended: true, // Collapsed by default
    rejected: true, // Collapsed by default
  });
  
  private applicationsWithData = computed(() => {
    return this.applications().map(app => {
      const rawData = app.onboarding_application_data;
      let mergedData: Record<string, any> | null = null;
      
      if (rawData) {
        const dataArray = Array.isArray(rawData) ? rawData : [rawData];
        if (dataArray.length > 0) {
            const reducedData = dataArray.reduce((acc, curr) => {
                if (curr && typeof curr === 'object') {
                    return { ...acc, ...curr };
                }
                return acc;
            }, {});
            if (Object.keys(reducedData).length > 0) {
                mergedData = reducedData;
            }
        }
      }

      let latestWebhook: OnboardingWebhook | null = null;
      if (app.onboarding_webhook_queue && app.onboarding_webhook_queue.length > 0) {
        // Create a copy before sorting to avoid mutating the original signal data.
        latestWebhook = [...app.onboarding_webhook_queue].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
      }
      
      return { ...app, mergedData, latestWebhook };
    });
  });

  private filteredAndSortedApps = computed(() => {
    const term = this.searchTerm().toLowerCase();
    const sort = this.sortOption();
    let apps = this.applicationsWithData();

    // 1. Filter
    if (term) {
      apps = apps.filter(app => {
        const fleetName = app.mergedData?.['fleet_name']?.toLowerCase() || '';
        const contactName = app.mergedData?.['contact_name']?.toLowerCase() || '';
        const contactEmail = app.mergedData?.['contact_email']?.toLowerCase() || '';
        const id = app.id.toLowerCase();

        return fleetName.includes(term) || 
               contactName.includes(term) || 
               contactEmail.includes(term) || 
               id.includes(term);
      });
    }

    // 2. Sort (create a new array to avoid in-place mutation of a signal's derived value)
    return [...apps].sort((a, b) => {
      switch (sort) {
        case 'created_at_desc':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'created_at_asc':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'updated_at_desc':
        default:
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
    });
  });

  draftApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'draft'));
  submittedApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'submitted'));
  inReviewApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'in_review'));
  approvedApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'approved'));
  readyForGoLiveApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'ready_for_go_live'));
  liveApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'live'));
  suspendedApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'suspended'));
  rejectedApps = computed(() => this.filteredAndSortedApps().filter(app => app.status === 'rejected'));

  constructor() {
    this.loadApplications();
    this.channels.push(
      this.supabaseService.subscribeToTableChanges(
        'onboarding_applications',
        'public',
        'applications-list',
        () => this.loadApplications()
      )
    );
     this.channels.push(
      this.supabaseService.subscribeToTableChanges(
        'onboarding_webhook_queue',
        'public',
        'webhooks-list',
        () => this.loadApplications()
      )
    );
  }

  ngOnDestroy(): void {
    this.channels.forEach(channel => this.supabaseService.unsubscribeFromChannel(channel));
  }

  async loadApplications() {
    this.loading.set(true);
    this.error.set(null);
    try {
      const data = await this.supabaseService.getApplications();
      console.log('[ApplicationListComponent] Data received from SupabaseService:', data);
      this.applications.set(data);
    } catch (e) {
      this.error.set('Failed to load applications.');
      console.error(e);
    } finally {
      this.loading.set(false);
    }
  }

  toggleColumn(column: ColumnKey) {
    this.columnState.update(state => ({
        ...state,
        [column]: !state[column]
    }));
  }

  onSearch(value: string) {
    this.searchTerm.set(value);
  }
  
  onSort(value: string) {
    this.sortOption.set(value as SortOption);
  }

  async logout() {
    try {
      await this.supabaseService.signOut();
      this.router.navigate(['/login']);
    } catch(e) {
      console.error('Logout failed', e);
    }
  }

  getStatusClass(status: Status): string {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'submitted':
        return 'bg-blue-100 text-blue-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'in_review':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  }
  
  getBuildStatus(webhook: OnboardingWebhook | null | undefined): string {
    if (!webhook) {
      return 'Awaiting Build';
    }
    switch (webhook.status) {
      case 'sent': return 'Build Complete';
      case 'in_progress': return 'Build In Progress';
      case 'processing': return 'Build In Progress';
      case 'pending': return 'Build Pending';
      case 'failed': return 'Build Failed';
      default: return 'Awaiting Build';
    }
  }

  getBuildStatusClass(webhook: OnboardingWebhook | null | undefined): string {
    if (!webhook) {
      return 'bg-slate-100 text-slate-800';
    }
    switch (webhook.status) {
      case 'sent': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-cyan-100 text-cyan-800 animate-pulse';
      case 'processing': return 'bg-cyan-100 text-cyan-800 animate-pulse';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
    });
  }

  viewApplication(id: string) {
    this.router.navigate(['/applications', id]);
  }

  promptDelete(event: Event, app: { id: string; mergedData?: Record<string, any> | null }) {
    event.stopPropagation();
    this.deleteError.set(null);
    this.appToDelete.set({
      id: app.id,
      name: app.mergedData?.['fleet_name'] || 'Unnamed Fleet'
    });
  }

  cancelDelete() {
    this.appToDelete.set(null);
    this.deleteError.set(null);
  }

  async confirmDelete() {
    const target = this.appToDelete();
    if (!target) return;

    this.isDeleting.set(true);
    this.deleteError.set(null);
    try {
      await this.supabaseService.deleteApplication(target.id);
      this.appToDelete.set(null);
      // Realtime subscription removes the card automatically
    } catch (e: any) {
      this.deleteError.set(e.message || 'Failed to delete application.');
    } finally {
      this.isDeleting.set(false);
    }
  }
}
